import {
  formatCompactAmPmTime,
  formatDate,
  getPayrollWeekDates,
  isLocalWeekday,
} from "../../../utils/datetime";
import { formatHoursCompact } from "../../../utils/timekeeping";
import { compareSortValues } from "../../../utils/collections/sort";
import { buildCsvString } from "../../../utils/files/csv";

/**
 * Format one worker shift as `7:00am-3:00pm (8 hours)`.
 *
 * @param {Object} shift Compact shift from the week-summary API.
 * @param {string} shift.timeIn Worker clock-in (HH:MM).
 * @param {string} shift.timeOut Worker clock-out (HH:MM).
 * @param {number} shift.hours Worker hours for the shift.
 * @returns {string} Single-shift summary cell text.
 */
export function formatWeekSummaryShift(shift) {
  const hoursLabel = `${formatHoursCompact(shift.hours)} hours`;
  const timeIn = formatCompactAmPmTime(shift.timeIn);
  const timeOut = formatCompactAmPmTime(shift.timeOut);
  if (!timeIn || !timeOut) {
    return hoursLabel;
  }
  return `${timeIn}-${timeOut} (${hoursLabel})`;
}

/**
 * Format a day column header like `Monday 9/14`.
 *
 * @param {Date} date Local calendar date.
 * @returns {string} Weekday plus unpadded month/day.
 */
export function formatWeekSummaryDayHeader(date) {
  const weekday = formatDate(date, "weekday");
  return `${weekday} ${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * Return whether any worker has a shift on the given ISO date.
 *
 * @param {string} dateKey YYYY-MM-DD.
 * @param {Array<Object>} workers Week-summary worker rows.
 * @returns {boolean} True when at least one shift falls on that day.
 */
function dayHasShifts(dateKey, workers) {
  return workers.some((worker) =>
    (worker.shifts || []).some((shift) => shift.date === dateKey)
  );
}

/**
 * Build day columns for the summary CSV.
 *
 * Monday-Friday always appear. Saturday and Sunday are included only when
 * someone worked that day, so typical weeks match the Mon-Fri manager sheet.
 *
 * @param {string} weekEnding Week-ending date (YYYY-MM-DD).
 * @param {Array<Object>} workers Week-summary worker rows.
 * @returns {Array<{dateKey: string, header: string}>} Day columns in order.
 */
export function getWeekSummaryDayColumns(weekEnding, workers = []) {
  return getPayrollWeekDates(weekEnding)
    .filter((date) => {
      if (isLocalWeekday(date)) {
        return true;
      }
      return dayHasShifts(formatDate(date, "YYYY-MM-DD"), workers);
    })
    .map((date) => ({
      dateKey: formatDate(date, "YYYY-MM-DD"),
      header: formatWeekSummaryDayHeader(date),
    }));
}

/**
 * Join a worker's shifts for one day. Days with no hours are left blank.
 *
 * @param {Array<Object>} shifts Shifts on a single day, already ordered.
 * @returns {string} Day cell text, or an empty string when the job has no hours.
 */
export function formatWeekSummaryDayCell(shifts = []) {
  if (!shifts.length) {
    return "";
  }
  return shifts.map(formatWeekSummaryShift).join(" - ");
}

/**
 * Format a job cell as `{number} - {description}`.
 *
 * Description is the job title shown in app dropdowns. Missing parts are
 * omitted; a shift with no job at all becomes a dash.
 *
 * @param {string} [jobNumber] Worker-entered job number.
 * @param {string} [jobDescription] Worker-entered job description.
 * @returns {string} Job cell text.
 */
export function formatWeekSummaryJobLabel(jobNumber = "", jobDescription = "") {
  const number = jobNumber || "";
  const description = jobDescription || "";
  if (number && description) {
    return `${number} - ${description}`;
  }
  return number || description || "-";
}

/**
 * Sort workers by primary job, then by display name.
 *
 * @param {Object} left Week-summary worker row.
 * @param {Object} right Week-summary worker row.
 * @returns {number} Negative when `left` sorts first.
 */
function compareWeekSummaryWorkers(left, right) {
  const jobCompare = compareSortValues(left.primaryJob, right.primaryJob);
  if (jobCompare !== 0) {
    return jobCompare;
  }
  return compareSortValues(left.workerName, right.workerName);
}

/**
 * Group one worker's shifts into a bucket per job.
 *
 * @param {Array<Object>} shifts Compact shifts for one worker.
 * @returns {Array<{jobNumber: string, jobDescription: string, shifts: Array<Object>}>} Job groups in first-seen order.
 */
function groupWeekSummaryShiftsByJob(shifts) {
  const grouped = Map.groupBy(shifts, (shift) =>
    shift.jobId != null ? `id:${shift.jobId}` : `number:${shift.jobNumber || ""}`
  );
  return Array.from(grouped.values(), (jobShifts) => ({
    jobNumber: jobShifts[0].jobNumber || "",
    jobDescription: jobShifts[0].jobDescription || "",
    shifts: jobShifts,
  }));
}

/**
 * Put the primary job first, then remaining jobs by number.
 *
 * @param {{jobNumber: string}} left Job group.
 * @param {{jobNumber: string}} right Job group.
 * @param {string} [primaryJob] Worker's primary job number.
 * @returns {number} Negative when `left` sorts first.
 */
function compareWeekSummaryJobGroups(left, right, primaryJob) {
  const primary = primaryJob || "";
  const leftIsPrimary = Boolean(primary) && left.jobNumber === primary;
  const rightIsPrimary = Boolean(primary) && right.jobNumber === primary;
  if (leftIsPrimary !== rightIsPrimary) {
    return leftIsPrimary ? -1 : 1;
  }
  return compareSortValues(left.jobNumber, right.jobNumber);
}

/**
 * Sum one hour field across a job's shifts.
 *
 * A total of 0 is left blank so unused RT, OT, or DT cells stay empty.
 *
 * @param {Array<Object>} shifts Shifts that share a job.
 * @param {"rt"|"ot"|"dt"} field Hour field to total.
 * @returns {string} Compact payroll hours, or an empty string when the total is 0.
 */
function sumWeekSummaryHours(shifts, field) {
  const total = shifts.reduce((sum, shift) => sum + (Number(shift[field]) || 0), 0);
  if (!total) {
    return "";
  }
  return formatHoursCompact(total);
}

/**
 * Day cells for one job, with that day's shifts ordered by clock-in.
 *
 * @param {Array<Object>} shifts Shifts for a single job.
 * @param {Array<{dateKey: string}>} dayColumns Day columns in sheet order.
 * @returns {Array<string>} One cell per day column.
 */
function buildJobDayCells(shifts, dayColumns) {
  return dayColumns.map((column) => {
    const dayShifts = shifts
      .filter((shift) => shift.date === column.dateKey)
      .slice()
      .sort((left, right) => compareSortValues(left.timeIn, right.timeIn));
    return formatWeekSummaryDayCell(dayShifts);
  });
}

/**
 * Build one CSV row per job this worker logged.
 *
 * The worker name is written only on the first row. That first row is the
 * primary job, so the name lines up with the job used to sort the sheet.
 *
 * @param {Object} worker Week-summary worker row.
 * @param {Array<{dateKey: string}>} dayColumns Day columns in sheet order.
 * @returns {Array<Array<string>>} CSV rows for this worker.
 */
function buildWorkerJobRows(worker, dayColumns) {
  const groups = groupWeekSummaryShiftsByJob(worker.shifts || []);
  groups.sort((left, right) => compareWeekSummaryJobGroups(left, right, worker.primaryJob));

  return groups.map((group, index) => [
    index === 0 ? worker.workerName || "" : "",
    formatWeekSummaryJobLabel(group.jobNumber, group.jobDescription),
    sumWeekSummaryHours(group.shifts, "rt"),
    sumWeekSummaryHours(group.shifts, "ot"),
    sumWeekSummaryHours(group.shifts, "dt"),
    ...buildJobDayCells(group.shifts, dayColumns),
  ]);
}

/**
 * Suggested download filename for a payout week summary.
 *
 * @param {string} weekEnding Week-ending date (YYYY-MM-DD).
 * @returns {string} CSV filename.
 */
export function getPayoutWeekSummaryFilename(weekEnding) {
  return `timesheet-summary-week-ending-${weekEnding}.csv`;
}

/**
 * Build CSV text for a payout week's worker-shift summary.
 *
 * Workers are ordered by primary job. Each job they worked is its own row.
 * Primary job is not a column.
 *
 * @param {Object} payload Week-summary API response.
 * @param {string} payload.weekEnding Saturday week-ending date.
 * @param {Array<Object>} payload.workers Worker rows with compact shifts.
 * @returns {string} CSV document body.
 */
export function buildPayoutWeekSummaryCsv(payload) {
  const workers = [...(payload.workers || [])].sort(compareWeekSummaryWorkers);
  const dayColumns = getWeekSummaryDayColumns(payload.weekEnding, workers);
  const headers = ["Name", "Job", "RT", "OT", "DT", ...dayColumns.map((column) => column.header)];
  const rows = workers.flatMap((worker) => buildWorkerJobRows(worker, dayColumns));
  return buildCsvString(headers, rows);
}
